import { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Table,
  Container,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import Header from "../../components/Headers/Header.js";
import helper from "../../services/helper.service";
import adminApi from "../../services/admin.service";
import customerService from "../../services/customer.service.js";
import {
  calculateGameProperties,
  sortGames,
} from "../../utils/games.js";
import { strTo12Hours, strTo24Hours } from "../../utils/date.js";

let interval = null;

const START_BOXES = [1, 22];

function ScoreReport(props) {
  const [games, setGames] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [sortedGames, setSortedGames] = useState([]);

  useEffect(() => {
    getClubList();
  }, []);

  useEffect(() => {
    if (selectedClub && selectedDate) {
      populateGames(selectedClub, selectedDate);
      interval && clearInterval(interval);
      interval = setInterval(() => {
        populateGames(selectedClub, selectedDate);
      }, 20000);
      return () => clearInterval(interval);
    }
  }, [selectedClub, selectedDate]);

  useEffect(() => {
    setSortedGames(sortGames(games, sortConfig));
  }, [games, sortConfig]);

  const getClubList = async () => {
    try {
      const response = await adminApi.getClubList({});
      if (response.status === 200) setClubs(response.data);
      else helper.showToast("Error", "Failed to fetch clubs.", 3);
    } catch (error) {
      console.error(error);
      helper.showToast("Error", "Unable to get clubs.", 3);
    }
  };

  const populateGames = async (clubId, date) => {
    try {
      const club = clubs.find((c) => c.id === parseInt(clubId));
      if (!club) return;
      
      const payload = { 
        club_id: club.id, 
        date,
        start_boxes: START_BOXES 
      };

      const boxTimesResponse = await customerService.getBoxTimeList({
        club_id: club.id,
      });
      const gamesResponse = await customerService.getGames(payload);

      if (gamesResponse.status === 200 && boxTimesResponse.status === 200) {
        const tempGames = calculateGameProperties(
          boxTimesResponse.data,
          gamesResponse.data,
          date
        );
        tempGames.forEach((game) => {
          game.club_name = club.name;
        });
        setGames(tempGames);
      } else {
        setGames([]);
      }
    } catch (error) {
      console.error(error);
      helper.showToast("Error", "Failed to fetch games.", 3);
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  // ✅ CSV DOWNLOAD FUNCTION
  const downloadCSV = () => {
    if (!sortedGames.length) {
      helper.showToast("Info", "No games to export.", 2);
      return;
    }

    const club = clubs.find((c) => c.id === parseInt(selectedClub));
    const clubName = club ? club.name : "Unknown Club";
    const reportDate = selectedDate || "Unknown Date";
    const fileName = `Score Report - ${clubName} - ${reportDate}.csv`;

    // Prepare CSV headers
    const headers = [
      "Game ID",
      "Tee Time",
      "Start Time",
      "Score 1",
      "Score 1 Time",
      "Score 2",
      "Score 2 Time",
      "Score 3",
      "Score 3 Time",
      "Score 4",
      "Score 4 Time",
      "Total Score",
    ];

    // Prepare CSV rows
    const rows = sortedGames.map((game) => [
      game.id,
      game.tee_time ? strTo12Hours(game.tee_time) : "",
      game.actual_time || "",
      game.score1 || "",
      game.score1Time || "",
      game.score2 || "",
      game.score2Time || "",
      game.score3 || "",
      game.score3Time || "",
      game.score4 || "",
      game.score4Time || "",
      game.totalScore || 0,
    ]);

    // Convert to CSV text
    const csvContent =
      [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Header count={games.length} lable={"Total Games"} />
      <Container className="mt--7" fluid>
        <Row>
          <Col xl="12">
            <Card className="shadow">
              <CardHeader className="bg-white border-0 d-flex flex-wrap justify-content-between align-items-center">
                <h3 className="mb-0">Games</h3>

                <div className="d-flex flex-wrap align-items-center">
                  {/* CLUB SELECT */}
                  <FormGroup className="mr-3 mb-2">
                    <Label for="clubSelect" className="mr-2 mb-0">
                      Select Club:
                    </Label>
                    <Input
                      type="select"
                      id="clubSelect"
                      value={selectedClub}
                      onChange={(e) => setSelectedClub(e.target.value)}
                    >
                      <option value="">-- Select Club --</option>
                      {clubs.map((club) => (
                        <option key={club.id} value={club.id}>
                          {club.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>

                  {/* DATE PICKER */}
                  <FormGroup className="mr-3 mb-2">
                    <Label for="dateSelect" className="mr-2 mb-0">
                      Select Date:
                    </Label>
                    <Input
                      type="date"
                      id="dateSelect"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </FormGroup>

                  {/* ✅ CSV DOWNLOAD BUTTON */}
                  <Button
                    color="success"
                    className="mb-2"
                    onClick={downloadCSV}
                    disabled={!sortedGames.length}
                  >
                    Download CSV Report
                  </Button>
                </div>
              </CardHeader>

              <CardBody style={{ padding: "0.5rem" }}>
                {selectedClub && selectedDate ? (
                  <div style={{ width: "100%", overflowX: "auto" }}>
                    <Table responsive hover size="sm" className="mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th onClick={() => handleSort("id")}>
                            ID {getSortIcon("id")}
                          </th>
                          <th onClick={() => handleSort("tee_time")}>
                            Tee Time {getSortIcon("tee_time")}
                          </th>
                          <th>Start Time</th>
                          <th>Score 1</th>
                          <th>Score 1 Time</th>
                          <th>Score 2</th>
                          <th>Score 2 Time</th>
                          <th>Score 3</th>
                          <th>Score 3 Time</th>
                          <th>Score 4</th>
                          <th>Score 4 Time</th>
                          <th>Total Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedGames.map((game) => (
                          <tr key={game.id}>
                            <td>{game.id}</td>
                            <td>
                              {game.tee_time ? strTo12Hours(game.tee_time) : ""}
                            </td>
                            <td>{game.actual_time || ""}</td>
                            <td>{game.score1}</td>
                            <td>{game.score1Time}</td>
                            <td>{game.score2}</td>
                            <td>{game.score2Time}</td>
                            <td>{game.score3}</td>
                            <td>{game.score3Time}</td>
                            <td>{game.score4}</td>
                            <td>{game.score4Time}</td>
                            <td style={{ fontWeight: "bold" }}>
                              {game.totalScore}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    Please select a club and date to view games.
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ScoreReport;
